export const ALPHABET_WRITING_RELEASE_BUNDLE_VERSION = 'alphabet.authoring_release_bundle.v1';
export const ALPHABET_WRITING_RUNTIME_V2 = 'alphabet.authoring_runtime.v2';
const REQUIRED_PROFILES = ['aiRuntime', 'audit', 'webRuntime'];
const SHA256_PATTERN = /^sha256:([a-f0-9]{64})$/;
export function parseReleaseBundleManifest(value) {
    const manifest = requireRecord(value, 'release bundle manifest');
    if (manifest.version !== ALPHABET_WRITING_RELEASE_BUNDLE_VERSION ||
        !isAlphabetCase(manifest.alphabetCase) ||
        manifest.playbackContract !== 'authoringStrokePlayback' ||
        manifest.projectBindingVersion !==
            'alphabet.authoring_project_binding.v2' ||
        manifest.runtimeVersion !== ALPHABET_WRITING_RUNTIME_V2) {
        throw new Error('release bundle manifest is incompatible');
    }
    const glyphs = requireString(manifest.glyphs, 'manifest.glyphs');
    const rendererVersion = requireString(manifest.rendererVersion, 'manifest.rendererVersion');
    const bundleContentHash = requireSha256(manifest.bundleContentHash, 'manifest.bundleContentHash');
    const releaseId = requireString(manifest.releaseId, 'manifest.releaseId');
    const validation = requireRecord(manifest.validation, 'manifest.validation');
    const filesValue = requireRecord(manifest.files, 'manifest.files');
    if (Object.keys(filesValue).length === 0) {
        throw new Error('manifest.files must not be empty');
    }
    const files = {};
    const paths = new Set();
    for (const [role, fileValue] of Object.entries(filesValue)) {
        if (!/^[A-Za-z][A-Za-z0-9]*$/.test(role)) {
            throw new Error(`invalid bundle file role ${role}`);
        }
        const file = requireRecord(fileValue, `manifest.files.${role}`);
        const filePath = requireBundleRelativePath(file.path, `manifest.files.${role}.path`);
        if (paths.has(filePath)) {
            throw new Error(`duplicate bundle file path ${filePath}`);
        }
        paths.add(filePath);
        files[role] = {
            byteLength: requireNonNegativeInteger(file.byteLength, `manifest.files.${role}.byteLength`),
            path: filePath,
            sha256: requireSha256(file.sha256, `manifest.files.${role}.sha256`),
        };
    }
    const profilesValue = requireRecord(manifest.profiles, 'manifest.profiles');
    const profiles = {};
    for (const requiredProfile of REQUIRED_PROFILES) {
        if (!(requiredProfile in profilesValue)) {
            throw new Error(`manifest.profiles.${requiredProfile} is required`);
        }
    }
    for (const [profile, rolesValue] of Object.entries(profilesValue)) {
        if (!Array.isArray(rolesValue) || rolesValue.length === 0) {
            throw new Error(`manifest.profiles.${profile} must not be empty`);
        }
        const roles = rolesValue.map((role, index) => requireString(role, `manifest.profiles.${profile}[${index}]`));
        if (new Set(roles).size !== roles.length) {
            throw new Error(`manifest.profiles.${profile} has duplicate roles`);
        }
        for (const role of roles) {
            if (!(role in files)) {
                throw new Error(`manifest.profiles.${profile} references unknown role ${role}`);
            }
        }
        profiles[profile] = [...roles].sort();
    }
    return {
        alphabetCase: manifest.alphabetCase,
        bundleContentHash,
        files,
        glyphs,
        playbackContract: 'authoringStrokePlayback',
        profiles: profiles,
        projectBindingVersion: 'alphabet.authoring_project_binding.v2',
        releaseId,
        rendererVersion,
        runtimeVersion: ALPHABET_WRITING_RUNTIME_V2,
        validation,
        version: ALPHABET_WRITING_RELEASE_BUNDLE_VERSION,
    };
}
export function parseCurrentReleaseSelectionV2(value) {
    const selection = requireRecord(value, 'current release selection');
    if (selection.version !== 'alphabet.authoring_current_release_selection.v2') {
        throw new Error('current release selection is incompatible');
    }
    const cases = requireRecord(selection.cases, 'current release selection cases');
    return {
        version: selection.version,
        cases: {
            lowercase: parseBindingSource(cases.lowercase, 'lowercase'),
            uppercase: parseBindingSource(cases.uppercase, 'uppercase'),
        },
    };
}
export function parseProjectBindingV2(value) {
    const binding = requireRecord(value, 'project binding');
    if (binding.version !== 'alphabet.authoring_project_binding.v2' ||
        !Array.isArray(binding.sources) ||
        binding.sources.length === 0) {
        throw new Error('project binding is incompatible');
    }
    const sources = binding.sources.map((source, index) => {
        const record = requireRecord(source, `project binding source ${index}`);
        if (!isAlphabetCase(record.alphabetCase)) {
            throw new Error(`project binding source ${index} has invalid case`);
        }
        return {
            alphabetCase: record.alphabetCase,
            bundleContentHash: requireSha256(record.bundleContentHash, `project binding source ${index}.bundleContentHash`),
            releaseId: requireString(record.releaseId, `project binding source ${index}.releaseId`),
        };
    });
    if (new Set(sources.map((source) => source.alphabetCase)).size !==
        sources.length) {
        throw new Error('project binding contains duplicate alphabet cases');
    }
    return { sources, version: binding.version };
}
export function createProjectBindingV2(word, selection) {
    const cases = new Set();
    for (const character of word) {
        if (/[a-z]/.test(character))
            cases.add('lowercase');
        if (/[A-Z]/.test(character))
            cases.add('uppercase');
    }
    if (cases.size === 0) {
        throw new Error('project word must contain an English alphabet character');
    }
    return {
        version: 'alphabet.authoring_project_binding.v2',
        sources: [...cases].sort().map((alphabetCase) => ({
            alphabetCase,
            ...selection.cases[alphabetCase],
        })),
    };
}
export async function buildReleaseBundleIdentity(descriptor) {
    const bundleContentHash = await sha256(new TextEncoder().encode(canonicalJson(descriptor)));
    const hex = SHA256_PATTERN.exec(bundleContentHash)?.[1];
    if (!hex)
        throw new Error('failed to calculate release bundle identity');
    return {
        bundleContentHash,
        releaseId: `${descriptor.alphabetCase}-bundle-${hex.slice(0, 16)}`,
    };
}
export async function verifyReleaseBundleFiles({ listFiles, manifest, readFile, }) {
    await verifyReleaseBundleIdentity(manifest);
    await verifyReleaseBundleRoles(manifest, Object.keys(manifest.files), readFile);
    const expectedFiles = [
        'manifest.v1.json',
        ...Object.values(manifest.files).map((file) => file.path),
    ].sort();
    const actualFiles = (await listFiles()).sort();
    if (canonicalJson(expectedFiles) !== canonicalJson(actualFiles)) {
        throw new Error('bundle file set mismatch');
    }
}
export async function verifyReleaseBundleProfile({ manifest, profile, readFile, }) {
    const roles = manifest.profiles[profile];
    if (!roles)
        throw new Error(`unknown release bundle profile ${profile}`);
    await verifyReleaseBundleIdentity(manifest);
    await verifyReleaseBundleRoles(manifest, roles, readFile);
}
export function assertReleaseBundleProfileRoles(manifest, profile, requiredRoles) {
    const roles = manifest.profiles[profile];
    if (!roles)
        throw new Error(`unknown release bundle profile ${profile}`);
    for (const role of requiredRoles) {
        if (!roles.includes(role)) {
            throw new Error(`release bundle profile ${profile} is missing role ${role}`);
        }
    }
}
async function verifyReleaseBundleIdentity(manifest) {
    const { bundleContentHash, releaseId, ...descriptor } = manifest;
    const expectedIdentity = await buildReleaseBundleIdentity(descriptor);
    if (bundleContentHash !== expectedIdentity.bundleContentHash ||
        releaseId !== expectedIdentity.releaseId) {
        throw new Error('release bundle identity mismatch');
    }
}
async function verifyReleaseBundleRoles(manifest, roles, readFile) {
    for (const role of roles) {
        const file = manifest.files[role];
        if (!file)
            throw new Error(`unknown release bundle role ${role}`);
        const bytes = await readFile(file.path);
        if (bytes.byteLength !== file.byteLength ||
            (await sha256(bytes)) !== file.sha256) {
            throw new Error(`bundle file mismatch for role ${role}`);
        }
    }
}
export function canonicalJson(value) {
    return `${JSON.stringify(sortValue(value), null, 2)}\n`;
}
function parseBindingSource(value, alphabetCase) {
    const source = requireRecord(value, `selection case ${alphabetCase}`);
    return {
        bundleContentHash: requireSha256(source.bundleContentHash, `selection case ${alphabetCase}.bundleContentHash`),
        releaseId: requireString(source.releaseId, `selection case ${alphabetCase}.releaseId`),
    };
}
function requireBundleRelativePath(value, label) {
    const filePath = requireString(value, label);
    const segments = filePath.split('/');
    if (filePath.startsWith('/') ||
        filePath.includes('\\') ||
        segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
        throw new Error(`${label} must be a normalized relative POSIX path`);
    }
    return filePath;
}
function requireRecord(value, label) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`${label} must be an object`);
    }
    return value;
}
function requireString(value, label) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`${label} must be a non-empty string`);
    }
    return value;
}
function requireSha256(value, label) {
    const hash = requireString(value, label);
    if (!SHA256_PATTERN.test(hash)) {
        throw new Error(`${label} must be a SHA-256 hash`);
    }
    return hash;
}
function requireNonNegativeInteger(value, label) {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`${label} must be a non-negative integer`);
    }
    return value;
}
function isAlphabetCase(value) {
    return value === 'lowercase' || value === 'uppercase';
}
function sortValue(value) {
    if (Array.isArray(value))
        return value.map(sortValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .sort(([left], [right]) => compareUnicodeCodePoints(left, right))
            .map(([key, nested]) => [key, sortValue(nested)]));
    }
    return value;
}
function compareUnicodeCodePoints(left, right) {
    const leftPoints = [...left].map((character) => character.codePointAt(0));
    const rightPoints = [...right].map((character) => character.codePointAt(0));
    const length = Math.min(leftPoints.length, rightPoints.length);
    for (let index = 0; index < length; index += 1) {
        const difference = leftPoints[index] - rightPoints[index];
        if (difference !== 0)
            return difference;
    }
    return leftPoints.length - rightPoints.length;
}
async function sha256(bytes) {
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
    return `sha256:${[...new Uint8Array(digest)]
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('')}`;
}
//# sourceMappingURL=release-bundle.js.map